import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import API from '@/api/api';

export function useSiteEstimationData(projectId, siteId, projectCode, estimateSiteInfoId) {
  const [loading, setLoading] = useState(true);
  const [projectInfo, setProjectInfo] = useState(null);
  const [siteInfo, setSiteInfo] = useState(null);
  const [projectDocuments, setProjectDocuments] = useState([]);
  const [siteEstimates, setSiteEstimates] = useState([]);

  const fetchSiteEstimateInfo = useCallback(async (signal) => {
    try {
      const response = await API.get(`/admin/estimates/sites/${estimateSiteInfoId}`, { signal });
      setSiteEstimates(response.data || []);
    } catch (err) {
      if (err.name !== 'CanceledError') {
        console.error('fetchSiteEstimateInfo 에러:', err);
        message.error('산정 회차 정보 조회 중 에러가 발생했습니다.');
      }
    }
  }, [estimateSiteInfoId]);

  const fetchData = useCallback(async (signal) => {
    try {
      setLoading(true);
      const projectPromise = API.get(`/projects?project_id=${projectId}`, { signal });
      const documentsPromise = API.get('projects/documents', { params: { project_code: projectCode }, signal });
      const sitesPromise = API.get(`/sites/${siteId}`, { signal });

      const [projectRes, documentsRes, sitesRes] = await Promise.all([
        projectPromise,
        documentsPromise,
        sitesPromise,
      ]);

      setProjectInfo(projectRes.data);
      setProjectDocuments(documentsRes.data || []);
      setSiteInfo(sitesRes.data);

      // 위 기본 정보가 모두 로드된 후, 산정 회차 정보를 불러옵니다.
      await fetchSiteEstimateInfo(signal);

    } catch (error) {
      if (error.name !== 'CanceledError') {
        message.error('사업장 산정 페이지 데이터 호출 중 장애가 발생했습니다.');
      }
    } finally {
      setLoading(false);
    }
  }, [projectId, siteId, projectCode, fetchSiteEstimateInfo]);

  useEffect(() => {
    const controller = new AbortController();
    if (projectId && siteId) {
      fetchData(controller.signal);
    }
    return () => {
      controller.abort();
    };
  }, [projectId, siteId, fetchData]);

  return {
    loading,
    projectInfo,
    siteInfo,
    projectDocuments,
    siteEstimates,
    refetch: fetchData, // 전체 데이터 리프레시 함수
    refetchEstimates: fetchSiteEstimateInfo, // 산정 회차만 리프레시하는 함수
  };
}
